import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import ImageUpload from '../../components/ImageUpload';

const PartnerServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [isLoader, setIsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    price: '',
    price_type: 'per_day',
    service_type: 'item',
    max_quantity: '',
    image: null,
  });

  useEffect(() => {
    if (isEdit) getService();
  }, [id]);

  const getService = async () => {
    try {
      setPageLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `partner/getService/${id}` });
      if (res.data.status) {
        const svc = res.data.data;
        setFormData({
          title:         svc.title || '',
          title_ar:      svc.title_ar || '',
          description:   svc.description || '',
          description_ar:svc.description_ar || '',
          price:         svc.price ?? '',
          price_type:    svc.price_type || 'per_day',
          service_type:  svc.service_type || 'item',
          max_quantity:  svc.max_quantity ?? '',
          image:         svc.image || null,
        });
      } else {
        toast.error(res.data.message);
      }
    } catch (_) {}
    finally { setPageLoader(false); }
  };

  const handleImageChange = (_, file) => setFormData(prev => ({ ...prev, image: file }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoader(true);
    try {
      const form = new FormData();
      form.append('title', formData.title);
      if (formData.title_ar)        form.append('title_ar', formData.title_ar);
      if (formData.description)     form.append('description', formData.description);
      if (formData.description_ar)  form.append('description_ar', formData.description_ar);
      if (formData.price !== '')    form.append('price', formData.price);
      form.append('price_type', formData.price_type);
      form.append('service_type', formData.service_type);
      // max_quantity only applies to items
      form.append('max_quantity', formData.service_type === 'item' ? formData.max_quantity : '');
      if (formData.image instanceof File) form.append('image', formData.image);

      const url = isEdit ? `partner/updateService/${id}` : 'partner/createService';
      const res = await ApiService.request({ method: 'POST', url, data: form });

      if (res.data.status) {
        toast.success(res.data.message);
        navigate('/partner/services');
      } else {
        toast.error(res.data.message);
      }
    } catch { /* silent */ }
    finally { setIsLoader(false); }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Toolbar */}
      <div className="app-toolbar py-3 py-lg-6">
        <div className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {isEdit ? 'Edit' : 'Add'} Service
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/partner/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to="/partner/services" className="text-muted text-hover-primary">Services</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">{isEdit ? 'Edit' : 'Add'}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="app-content flex-column-fluid">
        <div className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-6">
              {pageLoader ? (
                <div className="text-center py-10"><Loader size={30} text="Loading" color="black" /></div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-9">

                    {/* Title */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">Title</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Service title"
                        required
                        value={formData.title}
                        onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>

                    {/* Title Arabic */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Title (Arabic)</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="عنوان الخدمة"
                        dir="rtl"
                        value={formData.title_ar}
                        onChange={e => setFormData(prev => ({ ...prev, title_ar: e.target.value }))}
                      />
                    </div>

                    {/* Description */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Description</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder="Service description"
                        value={formData.description}
                        onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>

                    {/* Description Arabic */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Description (Arabic)</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder="وصف الخدمة"
                        dir="rtl"
                        value={formData.description_ar}
                        onChange={e => setFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                      />
                    </div>

                    {/* Price */}
                    <div className="col-md-3 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Price</label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-0 text-muted">AED</span>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={e => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Price Type */}
                    <div className="col-md-3 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Price Type</label>
                      <select
                        className="form-select form-select-solid"
                        value={formData.price_type}
                        onChange={e => setFormData(prev => ({ ...prev, price_type: e.target.value }))}
                      >
                        <option value="per_hour">Per Hour</option>
                        <option value="per_day">Per Day</option>
                      </select>
                    </div>

                    {/* Service Type */}
                    <div className="col-md-3 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Service Type</label>
                      <select
                        className="form-select form-select-solid"
                        value={formData.service_type}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          service_type: e.target.value,
                          // clear max_quantity when switching to package
                          max_quantity: e.target.value === 'item' ? prev.max_quantity : '',
                        }))}
                      >
                        <option value="item">Item</option>
                        <option value="package">Package</option>
                      </select>
                    </div>

                    {/* Max Quantity — only for items */}
                    {formData.service_type === 'item' && (
                      <div className="col-md-3 fv-row">
                        <label className="fs-6 fw-semibold mb-2">Max Quantity</label>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="e.g. 10"
                          min="1"
                          step="1"
                          value={formData.max_quantity}
                          onChange={e => setFormData(prev => ({ ...prev, max_quantity: e.target.value }))}
                        />
                      </div>
                    )}

                    {/* Image */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Image</label>
                      <ImageUpload
                        name="image"
                        onImageChange={handleImageChange}
                        defaultPreviewUrl={formData.image}
                      />
                    </div>

                  </div>

                  {/* Actions */}
                  <div className="text-center mt-9 mb-3">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => navigate('/partner/services')}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-base"
                      disabled={isLoader}
                    >
                      {isLoader ? <Loader size={20} text="Saving" /> : (isEdit ? 'Update Service' : 'Create Service')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerServiceForm;
